import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {IconTooltip} from '@forecast-it/design-system';
import {C, F} from './versionsStyles';
import {formatHours, formatMoney, formatPercent, targetProgress, VersionSummary} from './baselineVersionsMath';

export interface BaselineVersionBannerProps {
	summary: VersionSummary;
	currencySymbol: string;
	/** The project's fixed price when it is a fixed-price project, else null. */
	fixedPrice: number | null;
	/** The project's Baseline target, for time-and-materials projects. */
	baselineTarget: number | null;
	usesFixedPriceForRevenue: boolean;
}

const Navy = styled.div`
	background: ${C.navy};
	color: ${C.onDark};
	border-radius: 8px;
	padding: 20px 32px;
	margin-bottom: 24px;
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const Cells = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
	gap: 16px;
`;

const Cell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const Label = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	align-self: flex-start;
	color: ${C.navyLabel};
	${F.body2}
`;

const InfoIcon = styled.span`
	display: flex;
	filter: invert(1);
	opacity: 0.7;
`;

const Value = styled.span`
	color: ${C.onDark};
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
	${F.h4}
`;

const TargetRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	column-gap: 24px;
	row-gap: 8px;
	padding-top: 16px;
	border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const Track = styled.div`
	height: 6px;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.2);
	overflow: hidden;
`;

const Fill = styled.div<{$percent: number}>`
	width: ${p => p.$percent}%;
	height: 100%;
	border-radius: 3px;
	background: ${C.fill};
`;

const Muted = styled.span`
	color: ${C.navyLabel};
	white-space: nowrap;
	${F.body2}
`;

const Strong = styled.span`
	color: ${C.onDark};
	margin-left: 8px;
	${F.sub2}
`;

export const BaselineVersionBanner = ({
	summary,
	currencySymbol,
	fixedPrice,
	baselineTarget,
	usesFixedPriceForRevenue,
}: BaselineVersionBannerProps) => {
	const {formatMessage} = useIntl();
	const money = (n: number | null) => formatMoney(n ?? 0, currencySymbol);
	const cells: Array<[string, string, string]> = [
		['hours', `${formatHours(summary.minutes ?? 0)}h`, 'tip_hours'],
		['value_of_service', money(summary.valueOfService), 'tip_value_of_service'],
		['revenue', money(summary.revenue), usesFixedPriceForRevenue ? 'tip_revenue_fixed' : 'tip_revenue'],
	];
	if (summary.cost != null) {
		cells.push(['cost', money(summary.cost), 'tip_cost']);
		cells.push(['profit', money(summary.profit), 'tip_profit']);
		cells.push(['margin', `${formatPercent(summary.margin ?? 0)}%`, 'tip_margin']);
	}

	const isFixedPrice = fixedPrice != null && fixedPrice > 0;
	const target = isFixedPrice ? fixedPrice : baselineTarget != null && baselineTarget > 0 ? baselineTarget : null;
	const compared = isFixedPrice ? summary.valueOfService ?? 0 : summary.revenue ?? 0;
	const progress = target == null ? null : targetProgress(target, compared);

	return (
		<Navy data-cy="baseline-version-banner">
			<Cells>
				{cells.map(([key, value, tip]) => (
					<Cell key={key}>
						<Label>
							<span>{formatMessage({id: `baseline.versions.metric_${key}`})}</span>
							<InfoIcon>
								<IconTooltip icon="info" iconSize="s" width={IconTooltip.WIDTH.l}>
									{formatMessage({id: `baseline.versions.${tip}`})}
								</IconTooltip>
							</InfoIcon>
						</Label>
						<Value data-cy={`baseline-version-${key}`}>{value}</Value>
					</Cell>
				))}
			</Cells>
			{progress && target != null ? (
				<TargetRow>
					<span>
						<Muted>{formatMessage({id: 'baseline.versions.target'})}</Muted>
						<Strong>{money(target)}</Strong>
					</span>
					<Track
						role="progressbar"
						aria-label={formatMessage({
							id: isFixedPrice
								? 'baseline.versions.target_progress_value'
								: 'baseline.versions.target_progress_revenue',
						})}
						aria-valuenow={Math.round(progress.percent)}
						aria-valuemin={0}
						aria-valuemax={100}
					>
						<Fill $percent={progress.percent} />
					</Track>
					<Muted>
						{progress.difference > 0
							? formatMessage({id: 'baseline.versions.below_target'}, {amount: money(progress.difference)})
							: progress.difference < 0
							? formatMessage({id: 'baseline.versions.above_target'}, {amount: money(-progress.difference)})
							: formatMessage({id: 'baseline.versions.on_target'})}
					</Muted>
				</TargetRow>
			) : null}
		</Navy>
	);
};
